/**
 * @fileoverview Pure cycle calculation logic for the Period Calculator.
 * Contains all date and phase calculations without DOM dependencies.
 * @module period-tracker/period-cycle-logic
 */

/* global femcare */

femcare.periodTracker = femcare.periodTracker || {};

/**
 * @namespace femcare.periodTracker.cycleLogic
 * @description Handles all menstrual cycle calculations and phase determinations.
 */
femcare.periodTracker.cycleLogic = {
    lastMenstruationDay: null,
    monthNames: null,
    calculatedCycleDates: {
        menstruationDates: [],
        follicularDates: [],
        ovulationDates: [],
        lutealDates: [],
        minDate: null,
        maxDate: null
    },

    /**
     * Initializes the cycle logic with configuration.
     * @param {Object} config - Configuration object
     * @param {number} [config.menstrualDuration] - Duration of menstruation
     * @param {number} [config.menstrualCycle] - Total cycle length
     * @param {Date} [config.lastMenstruationDay] - Last period start date
     * @param {string[]} [config.monthNames] - Localized month names
     */
    init: function (config) {
        const defaults = femcare.periodTracker.config.defaults;

        this.menstrualDuration = config.menstrualDuration || defaults.menstrualDuration;
        this.menstrualCycle = config.menstrualCycle || defaults.menstrualCycle;

        if (config.lastMenstruationDay) {
            this.lastMenstruationDay = config.lastMenstruationDay;
        }
        if (config.monthNames) {
            this.monthNames = config.monthNames;
        }

        this.preCalculateAllCycleDates();
    },

    /**
     * Updates configuration and recalculates dates.
     * @param {Object} config - Configuration to update
     */
    updateConfig: function (config) {
        this.init(config);
    },

    /**
     * Pre-calculates all cycle dates for the configured date range.
     */
    preCalculateAllCycleDates: function () {
        if (!this.lastMenstruationDay) {
            console.warn('No last menstruation day set, cannot pre-calculate dates');
            return;
        }

        const phaseDays = femcare.periodTracker.config.phaseDays;
        const monthsToCalculate = femcare.periodTracker.config.defaults.monthsToCalculate;

        this.calculatedCycleDates = {
            menstruationDates: [],
            follicularDates: [],
            ovulationDates: [],
            lutealDates: [],
            minDate: null,
            maxDate: null
        };

        const today = new Date();
        const startDate = new Date(today.getFullYear(), today.getMonth() - monthsToCalculate, 1, 12, 0, 0, 0);
        const endDate = new Date(today.getFullYear(), today.getMonth() + monthsToCalculate + 1, 0, 12, 0, 0, 0);

        this.calculatedCycleDates.minDate = new Date(startDate);
        this.calculatedCycleDates.maxDate = new Date(endDate);

        let currentMenstruationStart = new Date(this.lastMenstruationDay);
        const cycleMs = this.menstrualCycle * 24 * 60 * 60 * 1000;

        while (currentMenstruationStart > startDate) {
            currentMenstruationStart = new Date(currentMenstruationStart.getTime() - cycleMs);
        }

        while (currentMenstruationStart <= endDate) {
            const cycleStartTime = currentMenstruationStart.getTime();
            const dayMs = 24 * 60 * 60 * 1000;

            // Menstruation phase
            for (let dayIndex = 0; dayIndex < this.menstrualDuration; dayIndex++) {
                const date = new Date(cycleStartTime + (dayIndex * dayMs));
                if (date >= startDate && date <= endDate) {
                    this.calculatedCycleDates.menstruationDates.push(this.formatDateKey(date));
                }
            }

            // Follicular phase
            for (let dayIndex = 0; dayIndex < phaseDays.follicular; dayIndex++) {
                const dayOffset = this.menstrualDuration + dayIndex;
                const date = new Date(cycleStartTime + (dayOffset * dayMs));
                if (date >= startDate && date <= endDate) {
                    this.calculatedCycleDates.follicularDates.push(this.formatDateKey(date));
                }
            }

            // Ovulation phase
            for (let dayIndex = 0; dayIndex < phaseDays.ovulation; dayIndex++) {
                const dayOffset = this.menstrualDuration + phaseDays.follicular + dayIndex;
                const date = new Date(cycleStartTime + (dayOffset * dayMs));
                if (date >= startDate && date <= endDate) {
                    this.calculatedCycleDates.ovulationDates.push(this.formatDateKey(date));
                }
            }

            // Luteal phase
            const lutealStartDay = this.menstrualDuration + phaseDays.follicular + phaseDays.ovulation;
            const lutealDays = this.menstrualCycle - lutealStartDay;
            for (let dayIndex = 0; dayIndex < lutealDays; dayIndex++) {
                const dayOffset = lutealStartDay + dayIndex;
                const date = new Date(cycleStartTime + (dayOffset * dayMs));
                if (date >= startDate && date <= endDate) {
                    this.calculatedCycleDates.lutealDates.push(this.formatDateKey(date));
                }
            }

            currentMenstruationStart = new Date(currentMenstruationStart.getTime() + cycleMs);
        }
    },

    /**
     * Formats a date as YYYY-MM-DD for lookup.
     * @param {Date} date - Date to format
     * @returns {string} Formatted date key
     */
    formatDateKey: function (date) {
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
    },

    /**
     * Gets the phase for a specific date.
     * @param {Date} date - Date to check
     * @returns {string} Phase name ('menstruation', 'follicular', 'ovulation', 'luteal')
     */
    getPhaseForDate: function (date) {
        const dateKey = this.formatDateKey(date);

        if (this.calculatedCycleDates.menstruationDates.includes(dateKey)) {
            return 'menstruation';
        }
        if (this.calculatedCycleDates.follicularDates.includes(dateKey)) {
            return 'follicular';
        }
        if (this.calculatedCycleDates.ovulationDates.includes(dateKey)) {
            return 'ovulation';
        }
        if (this.calculatedCycleDates.lutealDates.includes(dateKey)) {
            return 'luteal';
        }

        return 'follicular';
    },

    /**
     * Gets the cycle day number (1 to cycle length) for a date.
     * @param {Date} date - Date to check
     * @returns {number} Cycle day number
     */
    getCycleDayForDate: function (date) {
        if (!this.lastMenstruationDay) {
            return 1;
        }

        const daysSincePeriodStart = Math.floor((date - this.lastMenstruationDay) / (1000 * 60 * 60 * 24));
        let cycleDay = ((daysSincePeriodStart % this.menstrualCycle) + this.menstrualCycle) % this.menstrualCycle;
        return cycleDay + 1;
    },

    /**
     * Calculates the next period start date.
     * @param {Date} selectedDate - Reference date
     * @returns {Date|null} Next period start date
     */
    calculateNextPeriodDate: function (selectedDate) {
        if (!this.lastMenstruationDay) {
            return null;
        }

        const cycleDay = this.getCycleDayForDate(selectedDate);
        const daysUntilNextPeriod = cycleDay === 1
            ? this.menstrualCycle
            : this.menstrualCycle - cycleDay + 1;

        const nextPeriodDate = new Date(selectedDate);
        nextPeriodDate.setDate(selectedDate.getDate() + daysUntilNextPeriod);
        return nextPeriodDate;
    },

    /**
     * Calculates the next ovulation start date.
     * @param {Date} selectedDate - Reference date
     * @returns {Date|null} Next ovulation start date
     */
    calculateNextOvulationDate: function (selectedDate) {
        if (!this.lastMenstruationDay) {
            return null;
        }

        const phaseDays = femcare.periodTracker.config.phaseDays;
        const cycleDay = this.getCycleDayForDate(selectedDate);
        const ovulationStartDay = this.menstrualDuration + phaseDays.follicular + 1;

        const daysUntilOvulation = cycleDay < ovulationStartDay
            ? ovulationStartDay - cycleDay
            : this.menstrualCycle - cycleDay + ovulationStartDay;

        const nextOvulationDate = new Date(selectedDate);
        nextOvulationDate.setDate(selectedDate.getDate() + daysUntilOvulation);
        return nextOvulationDate;
    },

    /**
     * Gets phase information for a date.
     * @param {Date} date - Date to analyze
     * @param {Object} [phaseLabels] - Localized phase labels
     * @returns {Object} Phase info with phase, dayInPhase, totalPhaseDays, phaseName
     */
    getDatePhaseInfo: function (date, phaseLabels) {
        const phase = this.getPhaseForDate(date);
        const dateKey = this.formatDateKey(date);
        let dayInPhase = 1;
        let totalPhaseDays = 1;

        const relevantDates = this.calculatedCycleDates[phase + 'Dates'] || [];

        if (relevantDates.length > 0) {
            let phaseStartIndex = -1;
            let phaseEndIndex = -1;

            for (let i = 0; i < relevantDates.length; i++) {
                if (relevantDates[i] === dateKey) {
                    phaseEndIndex = i;

                    let j = i;
                    while (j > 0) {
                        const currentDate = new Date(relevantDates[j]);
                        const prevDate = new Date(relevantDates[j - 1]);
                        prevDate.setDate(prevDate.getDate() + 1);

                        if (currentDate.getTime() === prevDate.getTime()) {
                            j--;
                        } else {
                            break;
                        }
                    }
                    phaseStartIndex = j;

                    j = i;
                    while (j < relevantDates.length - 1) {
                        const currentDate = new Date(relevantDates[j]);
                        const nextDate = new Date(relevantDates[j + 1]);
                        currentDate.setDate(currentDate.getDate() + 1);

                        if (currentDate.getTime() === nextDate.getTime()) {
                            j++;
                            phaseEndIndex = j;
                        } else {
                            break;
                        }
                    }
                    break;
                }
            }

            if (phaseStartIndex >= 0 && phaseEndIndex >= 0) {
                dayInPhase = (phaseEndIndex - phaseStartIndex) - (phaseEndIndex - relevantDates.indexOf(dateKey)) + 1;
                totalPhaseDays = phaseEndIndex - phaseStartIndex + 1;
            }
        }

        return {
            phase: phase,
            dayInPhase: dayInPhase,
            totalPhaseDays: totalPhaseDays,
            phaseName: phaseLabels ? phaseLabels[phase] : phase
        };
    },

    /**
     * Gets phase color for a day.
     * @param {number} day - Day number in month
     * @param {Object} dayPhases - Map of day to phase
     * @returns {Object} Object with className and color
     */
    getPhaseForDay: function (day, dayPhases) {
        const phaseKey = dayPhases && dayPhases[day] ? dayPhases[day] : 'follicular';
        const phaseColors = femcare.periodTracker.config.phaseColors;

        return {
            className: phaseKey,
            color: phaseColors[phaseKey] || phaseColors.follicular
        };
    },

    /**
     * Calculates phases for each day in a month.
     * @param {Date} month - Month to calculate
     * @param {number} totalDays - Total days in month
     * @returns {Object} Object with dayPhases map, periodDays, and ovulationDays arrays
     */
    calculateMonthPhases: function (month, totalDays) {
        const dayPhases = {};
        const periodDays = [];
        const ovulationDays = [];

        for (let day = 1; day <= totalDays; day++) {
            const currentDate = new Date(month.getFullYear(), month.getMonth(), day, 12, 0, 0, 0);
            const phase = this.getPhaseForDate(currentDate);

            dayPhases[day] = phase;

            if (phase === 'menstruation') {
                periodDays.push(day);
            } else if (phase === 'ovulation') {
                ovulationDays.push(day);
            }
        }

        return { dayPhases, periodDays, ovulationDays };
    },

    /**
     * Checks if a month is within the calculated date range.
     * @param {Date} month - Month to check
     * @param {string} direction - 'prev' or 'next'
     * @returns {boolean} True if month is outside range
     */
    isMonthDisabled: function (month, direction) {
        if (direction === 'prev') {
            return this.calculatedCycleDates.minDate && month < this.calculatedCycleDates.minDate;
        }
        return this.calculatedCycleDates.maxDate && month > this.calculatedCycleDates.maxDate;
    },

    /**
     * Formats a date for display as "DD Month".
     * @param {Date} date - Date to format
     * @returns {string} Formatted date string
     */
    formatDateDisplay: function (date) {
        const day = date.getDate().toString().padStart(2, '0');
        const defaultMonthNames = ['January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'];
        const monthNames = this.monthNames || defaultMonthNames;
        return `${day} ${monthNames[date.getMonth()]}`;
    },

    /**
     * Gets ordinal suffix for a number.
     * @param {number} num - Number
     * @returns {string} Number with ordinal suffix
     */
    getOrdinal: function (num) {
        const suffixes = ['th', 'st', 'nd', 'rd'];
        const remainder = num % 100;
        return num + (suffixes[(remainder - 20) % 10] || suffixes[remainder] || suffixes[0]);
    }
};
